import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import AdminNavbar from './components/admin/AdminNavbar';

const LoginPage = React.lazy(() => import('./pages/admin/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/admin/DashboardPage'));
const FoldersPage = React.lazy(() => import('./pages/admin/FoldersPage'));
const FolderDetailPage = React.lazy(() => import('./pages/admin/FolderDetailPage'));
const PhotosPage = React.lazy(() => import('./pages/admin/PhotosPage'));
const PaymentsPage = React.lazy(() => import('./pages/admin/PaymentsPage'));
const PaymentDetailPage = React.lazy(() => import('./pages/admin/PaymentDetailPage'));
const UsersPage = React.lazy(() => import('./pages/admin/UsersPage'));
const UserDetailPage = React.lazy(() => import('./pages/admin/UserDetailPage'));
const ReportsPage = React.lazy(() => import('./pages/admin/ReportsPage'));
const SettingsPage = React.lazy(() => import('./pages/admin/SettingsPage'));
const PromosPage = React.lazy(() => import('./pages/admin/PromosPage'));
const PhotographersPage = React.lazy(() => import('./pages/admin/PhotographersPage'));
const PhotographerDetailPage = React.lazy(() => import('./pages/admin/PhotographerDetailPage'));
const PhotographerStripePage = React.lazy(() => import('./pages/admin/PhotographerStripePage'));
const PhotographerRegisterPage = React.lazy(() => import('./pages/PhotographerRegisterPage'));

const AdminRouteFallback: React.FC = () => (
  <div className="route-fallback route-fallback--admin" aria-busy="true" aria-label="Cargando">
    <div className="route-fallback-spinner" />
  </div>
);

const PendingBanner: React.FC = () => {
  const { isPendingPhotographer, user } = useAdminAuth();
  if (!isPendingPhotographer) return null;
  return (
    <div
      style={{
        background: '#fff3cd',
        color: '#664d03',
        padding: '0.75rem 1.25rem',
        textAlign: 'center',
        fontSize: '0.95rem',
        borderBottom: '1px solid #ffecb5',
      }}
    >
      Tu cuenta de fotógrafo ({user?.email}) está pendiente de aprobación. Puedes explorar el panel,
      pero no podrás crear carpetas ni subir fotos hasta que un administrador te apruebe.
    </div>
  );
};

const AdminProtectedLayout: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return (
    <div className="admin-app">
      <AdminNavbar />
      <PendingBanner />
      <Outlet />
    </div>
  );
};

const SuperAdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useAdminAuth();
  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <>{children}</>;
};

const AdminAppInner: React.FC = () => {
  return (
    <Suspense fallback={<AdminRouteFallback />}>
      <Routes>
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/fotografo/registro" element={<PhotographerRegisterPage />} />
        <Route path="/admin" element={<AdminProtectedLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="folders" element={<FoldersPage />} />
          <Route path="folders/:folderName" element={<FolderDetailPage />} />
          <Route path="photos" element={<PhotosPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/:id" element={<PaymentDetailPage />} />
          <Route path="stripe" element={<PhotographerStripePage />} />
          <Route
            path="photographers"
            element={
              <SuperAdminOnly>
                <PhotographersPage />
              </SuperAdminOnly>
            }
          />
          <Route
            path="photographers/:userId"
            element={
              <SuperAdminOnly>
                <PhotographerDetailPage />
              </SuperAdminOnly>
            }
          />
          <Route
            path="users"
            element={
              <SuperAdminOnly>
                <UsersPage />
              </SuperAdminOnly>
            }
          />
          <Route
            path="users/:userId"
            element={
              <SuperAdminOnly>
                <UserDetailPage />
              </SuperAdminOnly>
            }
          />
          <Route path="reports" element={<ReportsPage />} />
          <Route
            path="promos"
            element={
              <SuperAdminOnly>
                <PromosPage />
              </SuperAdminOnly>
            }
          />
          <Route
            path="settings"
            element={
              <SuperAdminOnly>
                <SettingsPage />
              </SuperAdminOnly>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

const AdminApp: React.FC = () => {
  return (
    <Router>
      <AdminAuthProvider>
        <AdminAppInner />
      </AdminAuthProvider>
    </Router>
  );
};

export default AdminApp;
